import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Exercise, ResourceType } from 'fitforce-document-sync';
import { cloneDeep, debounce } from 'lodash';

import { MatDialog } from '@angular/material/dialog';
import { User } from 'app/classes/user';
import { DvidshubApiService } from 'app/exercise-program/services/dvidshub-api.service';
import { ExerciseEditorGuard } from 'app/exercise-program/services/exercise-editor.guard';
import { AuthService } from 'app/services/auth.service';
import { Record } from 'fitforce-offline-first';
import { Subscription } from 'rxjs';
import { AddCategoryDialogComponent } from './add-category-dialog/add-category-dialog.component';
import { ConfirmationDialogComponent } from './confirmation-dialog/confirmation-dialog.component';
import { RemoveExerciseDialogComponent } from './remove-exercise-dialog/remove-exercise-dialog.component';


interface ParameterOption {
  name: string;
  unit: string;
  type: string;
}

const defaultParamType = 'int-array';

@Component({
  selector: 'exercise-editor',
  templateUrl: './exercise-editor.component.html',
  styleUrls: ['./exercise-editor.component.scss']
})
export class ExerciseLibraryEditorComponent implements OnInit, OnDestroy, OnChanges {
  private subs: Subscription[] = [];
  form: FormGroup = undefined;
  paramFormGroups: FormGroup[] = [];
  selectedCategories: Record[] = [];
  resourceUrl: string;
  resourceTitle: string;
  resourceDescription: string;
  resourceAssetId: string;
  user: User;
  autocompleteUnits: string[][] = [];
  selectedParam: number;
  originalName: string;
  embedUrl: SafeUrl;
  videoLoaded = false;
  debouncedSetEmbedUrl = debounce(this.setEmbedUrl.bind(this), 1000);
  loadingVideo: boolean;
  newExercise = false;

  @Input() categories: Record[];
  @Input() exercise: Exercise;
  @Input() exercises: Exercise[];

  defaultParameters: ParameterOption[] = [
    { name: 'reps', type: defaultParamType, unit: 'N/A' },
    { name: 'sets', type: defaultParamType, unit: 'N/A' },
    { name: 'load', type: defaultParamType, unit: 'percent' },
    { name: 'time', type: defaultParamType, unit: 'seconds' },
    { name: 'timer', type: defaultParamType, unit: 'seconds' },
    { name: 'distance', type: defaultParamType, unit: 'yards' },
    { name: 'hold', type: defaultParamType, unit: 'seconds' },
  ];

  get parameters(): FormArray {
    return this.form.get('formParameters') as FormArray;
  }

  get canEdit(): boolean {
    return this.exerciseEditorGuard.canEdit(this.user);
  }

  get defaultNames(): string[] {
    return this.defaultParameters.map(param => param.name);
  }

  get defaultUnits(): string[] {
    return this.defaultParameters.map(param => param.unit).filter(function (u, index, self) {
      return index === self.indexOf(u);
    });
  }

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private dvidshubService: DvidshubApiService,
    private exerciseEditorGuard: ExerciseEditorGuard
  ) {
  }

  ngOnInit() {
    this.authService.currentUserObs.subscribe(u => {
      this.user = u;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.exercise !== undefined) {
      this.buildParametersArray(this.exercise);
      this.buildCategories(this.exercise);
      this.buildResources(this.exercise);

      this.originalName = this.exercise.name;
    }
    this.newExercise = this.exercise && this.exercise.name ? false : true;
    this.form = this.formBuilder.group({
      formExerciseName: this.formBuilder.control(
        this.exercise ? this.exercise.name : undefined,
        [Validators.required, Validators.minLength(1), Validators.maxLength(128), this.duplicateNameValidator()]
      ),
      formCategories: this.formBuilder.control(this.selectedCategories ? this.selectedCategories : undefined),
      formParameters: this.formBuilder.array(this.paramFormGroups),
      formResourceUrl: this.formBuilder.control(this.resourceUrl ? this.resourceUrl : undefined, [this.dvidshubUrlValidator()]),
      formResourceTitle: this.formBuilder.control(this.resourceTitle ? this.resourceTitle : undefined),
      formResourceDescription: this.formBuilder.control(this.resourceDescription ? this.resourceDescription : undefined),
      formResourceAssetId: this.formBuilder.control(this.resourceAssetId ? this.resourceAssetId : undefined),
    });

    // set autocomplete suggestions for exercise parameters
    this.autocompleteUnits = new Array(this.paramFormGroups.length).fill('');
    this.autocompleteUnits.forEach((u, i) => {
      this.filterParamAutocomplete(this.paramFormGroups[i].value['name'], i);
    });

    this.videoLoaded = false;
    // load embedded video
    this.setEmbedUrl();


  }

  // get categories from existing exercise
  buildCategories(exercise: Exercise) {
    this.selectedCategories = [];
    if (exercise !== undefined && exercise.exercise_categories !== undefined) {
      for (const id of exercise.exercise_categories) {
        const category = this.categories.find(c => c.id === id);
        this.selectedCategories.push(category);
      }
    }
  }

  // get parameters from existing exercise and build formgroups
  buildParametersArray(exercise: Exercise) {
    if (exercise !== undefined) {
      this.paramFormGroups = [];
      for (const i in exercise.parameters) {
        if (exercise.parameters[i]) {
          const fg = this.formBuilder.group({
            name: this.formBuilder.control(i, [Validators.required]),
            unit: this.formBuilder.control(exercise.parameters[i]['unit']),
          });
          this.paramFormGroups.push(fg);
        }
      }
    } else {
      console.error('buildParametersArray: exercise is undefined');
    }
  }

  // get resource URL from existing exercise, or the first one if there are multiple
  buildResources(exercise: Exercise) {
    this.resourceUrl = '';
    if (exercise.resources && exercise.resources[0]) {
      this.resourceUrl = exercise.resources[0].hls_url;
      this.resourceTitle = exercise.resources[0].title;
      this.resourceAssetId = exercise.resources[0].asset_id;
    } else {
      this.resourceUrl = undefined;
      this.resourceTitle = undefined;
      this.resourceAssetId = undefined;
    }
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  addCategory() {
    this.dialog.open(AddCategoryDialogComponent)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          for (const r of result) {
            if (!this.selectedCategories.includes(r)) {
              this.selectedCategories.push(r);
            }
          }
        }
      });
  }

  removeCategory(category: Record) {
    const index = this.selectedCategories.indexOf(category);
    if (index !== -1) {
      this.selectedCategories.splice(index, 1);
    }
  }

  // create new empty parameter
  addParameter() {
    const fg = this.formBuilder.group({
      name: ['', Validators.required],
      unit: [''],
    });
    this.parameters.push(fg);

    this.filterParamAutocomplete('', this.parameters.length - 1);
  }

  removeParameter(param: FormGroup) {
    const index = this.paramFormGroups.indexOf(param);
    if (index !== -1) {
      this.paramFormGroups.splice(index, 1);
    }

    this.parameters.controls = this.parameters.controls.filter(p => p !== param);
    this.parameters.updateValueAndValidity();
  }

  // filter autocomplete results to show only appropriate suggestions based on parameter name,
  // and set unit to default if applicable
  filterParamAutocomplete(value: string, i: number) {
    this.selectedParam = i;
    const param = this.defaultParameters.find(p => p.name === value);
    if (param) {
      this.autocompleteUnits[i] = [param.unit];
      (this.parameters.controls[i] as FormGroup).controls.unit.setValue(param.unit);
    } else {
      this.autocompleteUnits[i] = this.defaultUnits;
    }

  }

  saveExercise() {
    const exercise = cloneDeep(this.exercise);
    // update exercise.name formExerciseName control
    exercise.name = this.form.get('formExerciseName').value;

    // map parameter controls to exercise.parameters
    const params = {};
    this.parameters.controls.forEach((group: FormGroup) => {
      const name = group.controls['name'].value;
      params[name] = {
        type: this.exercise.parameters[name] ? this.exercise.parameters[name]['type'] : defaultParamType,
        unit: group.controls['unit'].value.trim() === '' ? 'N/A' : group.controls['unit'].value,
      } as ParameterOption;
    });
    exercise.parameters = {
      // add edited parameters to exercise
      ...params
    };

    if (this.form.get('formResourceUrl').value) {
      exercise.resources[0] = {
        ...exercise.resources[0],
        hls_url: this.form.get('formResourceUrl').value,
        title: this.form.get('formResourceTitle').value,
        asset_id: this.form.get('formResourceAssetId').value,
        type: ResourceType.Video
      };
    } else {
      exercise.resources = [];
    }

    // map category id to exercise.categories
    exercise.exercise_categories = this.form.get('formCategories').value.map(value => value.id);

    this.exercise = exercise;

    this.dialog.open(ConfirmationDialogComponent, { data: exercise, disableClose: true })
      .afterClosed()
      .subscribe(result => { });
  }

  removeExercise(exercise) {
    this.dialog.open(RemoveExerciseDialogComponent, { data: this.exercise, disableClose: true })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.exercise = undefined;
        }
      });
  }

  duplicateNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        if (this.originalName && control.value.toLowerCase() === this.originalName.toLowerCase()) {
          return null;
        }
        if (this.exercises) {
          const ex = this.exercises.find(e => e.name.toLowerCase() === control.value.toLowerCase().trim());
          return ex ? { 'duplicateName': { value: control.value } } : null;
        }
      }
    };
  }

  dvidshubUrlValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === '' || control.value === null) {
        return null;
      }
      return this.videoLoaded ? null : { 'invalidUrl': { value: control.value } };
    };
  }

  /**
   * Pull video ID out of a dvidshub.net URL, load embedded video and fill asset ID field
   */
  setEmbedUrl() {
    const pathRegex: RegExp = /(dvidshub.net).*\/(\d+(?!=\/))/;
    const url = this.form.get('formResourceUrl').value;
    this.loadingVideo = true;
    if (url && pathRegex.test(url)) {

      const captures = pathRegex.exec(url);
      const videoId = captures[2];
      const newUrl = 'https://www.dvidshub.net/video/embed/' + videoId;
      this.embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newUrl);

      this.form.get('formResourceAssetId').setValue('video:' + videoId);

      this.dvidshubService.getVideoMetadata(videoId).subscribe(
        res => {
          this.videoLoaded = true;
          this.loadingVideo = false;
          this.form.get('formResourceTitle').setValue(res.title);
          this.form.get('formResourceDescription').setValue(res.description);
          this.form.get('formResourceUrl').updateValueAndValidity();

        }, err => {
          this.videoLoaded = false;
          this.loadingVideo = false;
          this.form.get('formResourceUrl').updateValueAndValidity();
        });
    } else {
      this.videoLoaded = false;
      this.loadingVideo = false;
      this.embedUrl = undefined;
      this.form.get('formResourceAssetId').setValue('');
      this.form.get('formResourceTitle').setValue('');
      this.form.get('formResourceDescription').setValue('');
      this.form.get('formResourceUrl').updateValueAndValidity();
    }
  }
}
